"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppSelector } from "../../_hooks/hooks";
import { ICMS } from "../home/homeModel";
import { X, ChevronDown } from "lucide-react";
import { Preloader } from "@/components/Preloader";
import { usePathname } from "next/navigation";
import { createLookupObject } from "@/_helper/helper";

interface FAQProps {
  cmsData?: any[];
  PropertyWithGalleyList: any;
}

export default function FAQClient({
  cmsData,
  PropertyWithGalleyList,
}: FAQProps) {
  const pathname = usePathname();
  const path = pathname?.split("/")?.[1];
  const loading = useAppSelector((state) => state.property.loading);
  const BannerList = useAppSelector((state) => state.home.BannerList);

  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const [activePropertyId, setActivePropertyId] = useState<number | null>(null);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === path) || {};

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // If a property has no FAQs
  const propertiesWithFAQs =
    PropertyWithGalleyList?.filter(
      (p: any) => p?.FaqList && p?.FaqList?.length > 0,
    ) || [];

  const handlePropertyClick = (propertyId: number) => {
    setActivePropertyId(activePropertyId === propertyId ? null : propertyId);
    setOpenFAQ(0);
  };

  useEffect(() => {
    if (activePropertyId === null && propertiesWithFAQs?.length > 0) {
      setActivePropertyId(propertiesWithFAQs[0]?.ID);
    }
  }, [propertiesWithFAQs]);

  const activeProperty = PropertyWithGalleyList?.find(
    (p: any) => p?.ID === activePropertyId,
  );

  return (
    <>
      <main className="min-h-screen bg-[#F9F7F4] text-md">
        {loading && <Preloader />}

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-end justify-center overflow-hidden text-lg">
          <div
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${banner?.BannerImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 mt-25 mb-15">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {banner?.SubTitle && (
                <div className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
                  <p className="text-[#C9A962] tracking-[0.4em] text-sm">
                    {banner?.SubTitle}
                  </p>
                </div>
              )}

              <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight font-playfair">
                {/* Let's Start a<br /> */}
                <span className="text-[#C9A962]">
                  {banner?.BannerTitle || "FAQs"}
                </span>
              </h1>

              {banner?.Description && (
                <p
                  className="text-white/95 text-xl md:text-2xl max-w-3xl mx-auto font-playfair"
                  dangerouslySetInnerHTML={{
                    __html: banner?.Description,
                  }}
                />
              )}
            </motion.div>
          </div>
        </section>

        {/* Property List Section */}
        <section className="py-20 relative z-20 -mt-10 bg-transparent">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {lookUpCMSData?.["FAQs - Section 1 : Title"] ? (
              <div
                className="text-center mb-16"
                dangerouslySetInnerHTML={{
                  __html: lookUpCMSData?.["FAQs - Section 1 : Title"]?.Template,
                }}
              />
            ) : (
              <div className="text-center mb-16">
                <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                  Got Questions?
                </p>
                <h2 className="text-2xl md:text-4xl mb-6 text-[#1A1A1A]">
                  Frequently Asked Questions
                </h2>
                <div className="w-24 h-1 bg-[#C9A962] mx-auto mb-8" />
                <p className="text-gray-600 text-xl max-w-2xl mx-auto">
                  Find answers to common questions about our properties,
                  services, and policies to help you plan the perfect stay.
                </p>
              </div>
            )}

            {/* Property Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {propertiesWithFAQs.map((property: any, index: number) => (
                <motion.div
                  key={property.ID}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group relative cursor-pointer rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex items-center justify-between p-6 transition-all duration-300 ${activePropertyId === property.ID ? "bg-[#C9A962] text-white" : "bg-gray-50 text-gray-400 group-hover:bg-[#C9A962]/10 group-hover:text-[#C9A962]"}`}
                  onClick={() => handlePropertyClick(property.ID)}
                >
                  <div className="flex-1">
                    <h3
                      className={`font-medium text-gray-900 group-hover:text-[#B42025] transition-colors ${activePropertyId === property.ID ? "!text-white" : ""}`}
                    >
                      {property.PropertyName}
                    </h3>
                  </div>
                  {/* <div
                    className={`p-3 rounded-full transition-colors ${activePropertyId === property.ID ? "bg-[#C9A962] text-white" : "bg-gray-50 text-gray-400 group-hover:bg-[#C9A962]/10 group-hover:text-[#C9A962]"}`}
                  >
                    <ImageIcon className="w-6 h-6" />
                  </div> */}
                </motion.div>
              ))}
            </div>

            {/* Expanded FAQs Grid for Selected Property */}
            <AnimatePresence mode="wait">
              {activePropertyId && activeProperty && (
                <motion.div
                  key={activeProperty.ID}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-16 bg-white rounded-3xl p-8 shadow-xl border border-gray-100 overflow-hidden"
                >
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-2xl font-medium font-light text-gray-900 border-l-4 border-[#C9A962] pl-4">
                      {activeProperty.PropertyName} FAQs
                    </h3>
                    {/* <button
                      onClick={() => setActivePropertyId(null)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="w-6 h-6 text-gray-500" />
                    </button> */}
                  </div>

                  <div className="space-y-6">
                    {activeProperty?.FaqList?.map((faq: any, index: number) => (
                      <div
                        key={index}
                        className="bg-white rounded-2xl overflow-hidden shadow-lg"
                      >
                        <button
                          onClick={() =>
                            setOpenFAQ(openFAQ === index ? null : index)
                          }
                          className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-xl text-[#1A1A1A] pr-4">
                            {faq?.Question}
                          </span>
                          <ChevronDown
                            className={`h-6 w-6 text-[#B42025] transition-transform flex-shrink-0 ${openFAQ === index ? "rotate-180" : ""}`}
                          />
                        </button>
                        {openFAQ === index && (
                          <div className="px-8 pb-6">
                            <p
                              className="text-gray-700 text-lg leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: faq?.Answer }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>
    </>
  );
}
